import axios, {AxiosResponse} from 'axios';
import {
    CaseInfoItemAuditTrailRetrieveResponse, CaseInfoItemsReterieveResponse, CaseInfoNarrativeRetrieveResponse,
    CaseInfoNarrativesRetrieveResponse,
    CaseInfoProductRetrieveResponse, CaseInfoProductsRetrieveResponse, CaseInfoReceiverRetrieveResponse,
    CaseInfoReceiversRetrieveResponse, CaseInfoReporterRetrieveResponse, CaseInfoReportersRetrieveResponse,
    CaseInfoSenderRetrieveResponse, CaseInfoSendersRetrieveResponse, CaseInfoStudiesRetrieveResponse,
    CaseInfoStudyRetrieveResponse, ReceiverSelectDialogRequestType
} from '@/model/CommonModel';
import ApiUtils from '@/utils/ApiUtils';

export default class CaseInfoApi {
    public static retrieveCaseInfoSenders(sponsorKey: number, params: any = {}): Promise<CaseInfoSendersRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/senders`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoSenderOptions(sponsorKey: string, params: any = {}): Promise<CaseInfoSendersRetrieveResponse> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/senders/options`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoItems(sponsorKey: string, type: string): Promise<CaseInfoItemsReterieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/categories/${type}/items`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseInfoSender(sponsorKey: number | string, senderKey: number | string, param: any) {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/sender/${senderKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoSender(sponsorKey: number, senderKey: number): Promise<CaseInfoSenderRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sender/${senderKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoReceivers(sponsorKey: number, params: any = {}): Promise<CaseInfoReceiversRetrieveResponse> {
        return axios.get(`/1.0/sponsors/${sponsorKey}/manufacturers`, {params})
                    .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoReceiverOptions(sponsorKey: string, type: ReceiverSelectDialogRequestType,
                                                  params: any = {}): Promise<CaseInfoReceiversRetrieveResponse> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/receivers/options?${type}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoReceiver(sponsorKey: number, receiverKey: number): Promise<CaseInfoReceiverRetrieveResponse> {
        return axios.get(`/1.0/sponsors/${sponsorKey}/manufacturers/${receiverKey}`)
                    .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseInfoReceiver(sponsorKey: number | string, receiverKey: number | string, param: any) {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/manufacturers/${receiverKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoProducts(sponsorKey: number | string, params: any = {}): Promise<CaseInfoProductsRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/products`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoProductOptions(sponsorKey: number | string, params: any = {}): Promise<CaseInfoProductsRetrieveResponse> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/products/options`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadCaseInfoProductTemplate(sponsorKey: number | string) {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/products/template-download`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }

    public static downloadCaseInfoProducts(sponsorKey: number | string): Promise<any> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/products/download`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static uploadCaseInfoProductBatchFile(sponsorKey: number | string, formData: FormData) {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/products`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoProduct(sponsorKey: string, productKey: string): Promise<CaseInfoProductRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/products/${productKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseInfoProduct(sponsorKey: string, productKey: string, param: any): Promise<CaseInfoProductRetrieveResponse> {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/products/${productKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoReporters(sponsorKey: string, params: any = {}): Promise<CaseInfoReportersRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/reporters`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoReporterOptions(sponsorKey: string, params: any = {}): Promise<CaseInfoReportersRetrieveResponse> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/reporters/options`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoReporter(sponsorKey: string, reporterKey: string): Promise<CaseInfoReporterRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/reporters/${reporterKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseInfoReporter(sponsorKey: string, reporterKey: string, param: any): Promise<CaseInfoReporterRetrieveResponse> {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/reporters/${reporterKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoStudies(sponsorKey: string, params: any = {}): Promise<CaseInfoStudiesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/studies`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseInfoStudiesOptions(sponsorKey: string, params: any = {}): Promise<CaseInfoStudiesRetrieveResponse> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/studies/options`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static downloadCaseInfoStudies(sponsorKey: string, params: any = {}): Promise<any> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/studies/download`, {params, responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static retrieveCaseInfoStudy(sponsorKey: string, studyKey: string): Promise<CaseInfoStudyRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/studies/${studyKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseInfoStudy(sponsorKey: string, studyKey: string, param: any): Promise<CaseInfoStudyRetrieveResponse> {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/studies/${studyKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveCaseInfoNarratives(sponsorKey: string, params: any = {})
            : Promise<CaseInfoNarrativesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/narratives`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveCaseInfoNarrativeOptions(sponsorKey: string, params: any = {})
        : Promise<CaseInfoNarrativesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/narratives/options`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveCaseInfoNarrative(sponsorKey: string, nrKey: string)
        : Promise<CaseInfoNarrativeRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/narratives/${nrKey}`)
            .then((res: AxiosResponse) => res.data);
    }

    public static saveCaseInfoNarrative(sponsorKey: string, nrKey: string, param: any): Promise<number> {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/narratives/${nrKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }

    public static getCaseInfoItemAuditTrails(sponsorKey: string, category: string, params: any = {})
                                                                    : Promise<CaseInfoItemAuditTrailRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/categories/${category}/audit-trail`, {params})
            .then((res: AxiosResponse) => res.data);
    }
}
